import React, { useState } from "react";
import { api, formatError, logDev } from "../../api";
import { FileText, FileX2, Loader2 } from "lucide-react";

/**
 * DownloadResumeButton — HR-side button that streams a candidate's resume
 * tied to a specific application. Renders as a compact icon button or a
 * full-width pill depending on the `compact` prop.
 *
 * Backend: GET /api/applications/{appId}/resume/download (HR + admin only)
 */
export default function DownloadResumeButton({
  appId,
  filename,
  compact = false,
  hasResume = true,
  className = "",
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const click = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!hasResume) return;
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/applications/${appId}/resume/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      // Try open in a new tab (view mode). Fall back to force-download if
      // the browser blocked the popup — user still gets the file either way.
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || `resume-${appId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      // Revoke after the new tab has had time to load.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e2) {
      // Same blob-error trick as ResumePreview so recruiters see the
      // friendly "please ask candidate to re-upload" message instead of
      // "Request failed with status code 4xx".
      let msg = "";
      try {
        const blob = e2?.response?.data;
        if (blob && typeof blob.text === "function") {
          const raw = await blob.text();
          try {
            const parsed = JSON.parse(raw);
            msg = parsed?.detail || parsed?.error || raw;
          } catch {
            msg = raw;
          }
        }
      } catch { /* fall through */ }
      if (!msg) msg = formatError(e2);
      setErr(msg);
      logDev.warn("[hr/resume-view]", msg);
    } finally {
      setBusy(false);
    }
  };

  if (!hasResume) {
    return (
      <span
        title="Candidate has not uploaded a resume"
        className={`inline-flex items-center gap-1 text-[11px] text-muted ${className}`}
        data-testid={`resume-missing-${appId}`}
      >
        <FileX2 size={12} /> No resume
      </span>
    );
  }

  if (compact) {
    return (
      <button
        type="button"
        onClick={click}
        disabled={busy}
        title={busy ? "Opening…" : "View resume"}
        aria-label="View resume"
        data-testid={`hr-resume-view-${appId}`}
        className={`w-9 h-9 grid place-items-center rounded-md text-muted hover:text-signal hover:bg-paper transition disabled:opacity-60 ${className}`}
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
      </button>
    );
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={click}
        disabled={busy}
        data-testid={`hr-resume-view-${appId}`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border bg-white text-signal hover:bg-paper transition disabled:opacity-60"
        style={{ borderColor: "rgb(0 108 255 / 0.3)" }}
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
        {busy ? "Opening…" : "View resume"}
      </button>
      {err && <div className="text-[11px] text-red-600 mt-1">{err}</div>}
    </div>
  );
}
